import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { User, UserRole } from '../types';
import { signIn, signOut } from '../utils/supabase/auth';
import { getCurrentUser, signup, checkAdminSetup, forceAdminSetup } from '../utils/supabase/api';
import { validateHumanName } from '../utils/nameValidator';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle, RefreshCw, Eye, EyeOff, Trophy, Gamepad2, Dumbbell, Target, Flame, Zap, Medal, Swords } from 'lucide-react';

const ADMIN_SETUP_CACHE_KEY = 'admin_setup_status';

/* ------------------------------------------------------------------ */
/*  Floating sport icon data                                          */
/* ------------------------------------------------------------------ */
const SPORT_ICONS = [
  { Icon: Trophy, color: 'text-amber-400', size: 28 },
  { Icon: Gamepad2, color: 'text-purple-400', size: 32 },
  { Icon: Dumbbell, color: 'text-emerald-400', size: 26 },
  { Icon: Target, color: 'text-red-400', size: 30 },
  { Icon: Flame, color: 'text-orange-400', size: 24 },
  { Icon: Zap, color: 'text-cyan-400', size: 28 },
  { Icon: Medal, color: 'text-yellow-300', size: 26 },
  { Icon: Swords, color: 'text-indigo-400', size: 30 },
];

interface FloatingIcon {
  id: number;
  iconIdx: number;
  x: number;
  y: number;
  delay: number;
  duration: number;
  scale: number;
}

function buildFloatingIcons(count: number): FloatingIcon[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    iconIdx: i % SPORT_ICONS.length,
    x: Math.random() * 100,
    y: Math.random() * 100,
    delay: Math.random() * 6,
    duration: 8 + Math.random() * 10,
    scale: 0.6 + Math.random() * 0.6,
  }));
}

interface LoginPageProps {
  onLogin: (user: User) => void | Promise<void>;
  initialTab?: 'login' | 'signup';
  pendingEquipmentName?: string;
}

export function LoginPage({ onLogin, initialTab = 'login', pendingEquipmentName }: LoginPageProps) {
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>(initialTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [department, setDepartment] = useState('');
  const [studentId, setStudentId] = useState('');
  const [role, setRole] = useState<UserRole>('student');
  const [isLoading, setIsLoading] = useState(false);
  const [showLoginHelp, setShowLoginHelp] = useState(false);
  const [loginNotice, setLoginNotice] = useState<{ title: string; description: string } | null>(null);
  const [signupNotice, setSignupNotice] = useState<string | null>(null);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const logoSrc = `${import.meta.env.BASE_URL}talaai.png`;

  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [adminSetupStatus, setAdminSetupStatus] = useState<{ checked: boolean; exists: boolean }>({
    checked: false,
    exists: false,
  });
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const floatingIcons = useMemo(() => buildFloatingIcons(14), []);
  const signupNameValidation = useMemo(() => validateHumanName(name), [name]);

  // Sync tab when initialTab prop changes (e.g., navigating from landing page)
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  useEffect(() => {
    setLoginNotice(null);
    setSignupNotice(null);
  }, [activeTab]);

  useEffect(() => {
    if (role !== 'student') {
      setStudentId('');
    }
  }, [role]);

  // Check admin setup on mount
  useEffect(() => {
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    const cachedAdminStatus = sessionStorage.getItem(ADMIN_SETUP_CACHE_KEY);

    if (cachedAdminStatus) {
      const adminExists = cachedAdminStatus === 'true';
      setAdminSetupStatus({ checked: true, exists: adminExists });
      return;
    }

    try {
      setIsCheckingAdmin(true);
      const { adminExists } = await checkAdminSetup();
      sessionStorage.setItem(ADMIN_SETUP_CACHE_KEY, String(adminExists));
      setAdminSetupStatus({ checked: true, exists: adminExists });
      if (!adminExists) {
        console.warn('⚠️ Admin account not found');
      }
    } catch (error) {
      console.error('Error checking admin setup:', error);
      setAdminSetupStatus({ checked: true, exists: false });
    } finally {
      setIsCheckingAdmin(false);
    }
  };

  const handleForceAdminSetup = async () => {
    try {
      setIsCheckingAdmin(true);
      const loadingToast = toast.loading('Recreating admin account...');
      
      console.log('🔄 Starting admin account recreation...');
      await forceAdminSetup();
      
      console.log('✅ Checking admin status...');
      await checkAdmin();
      sessionStorage.setItem(ADMIN_SETUP_CACHE_KEY, 'true');
      
      toast.dismiss(loadingToast);
      toast.success('✅ Admin account recreated! Login with: admin@nemsu.edu.ph / admin123', {
        duration: 6000,
      });
      
      // Pre-fill the login form
      setEmail('admin@nemsu.edu.ph');
      setPassword('admin123');
      setActiveTab('login');
    } catch (error: any) {
      console.error('❌ Error forcing admin setup:', error);
      toast.dismiss();
      toast.error(`Failed to setup admin: ${error.message || 'Unknown error'}`);
    } finally {
      setIsCheckingAdmin(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setIsLoading(true);
    setLoginNotice(null);
    try {
      console.log('🔐 Attempting login for:', email);
      
      const { data, error } = await signIn(email, password);
      
      if (error) {
        throw error;
      }

      if (!data?.session) {
        throw new Error('No session data returned from authentication');
      }

      console.log('✅ Sign in successful, getting user data...');

      // Get user data from backend
      const { user } = await getCurrentUser();
      
      if (!user) {
        console.error('❌ No user data returned from server');
        throw new Error('Failed to retrieve user data from server');
      }
      
      console.log('✅ User data retrieved:', user.email);
      await onLogin(user);
      toast.success('Login successful!');
    } catch (error: any) {
      if (error.message?.includes('pending admin verification')) {
        await signOut().catch(() => null);
        setLoginNotice({
          title: 'Coach Account Pending Approval',
          description: 'Your coach account has been created, but an administrator must verify it before you can log in. Please wait for the approval email.',
        });
        return;
      }

      console.error('❌ Login error:', error);
      
      // Scroll to admin section if admin login fails
      if (email === 'admin@nemsu.edu.ph') {
        setTimeout(() => {
          document.getElementById('admin-setup-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 800);
      }
      
      // Provide helpful error messages
      if (error.message?.includes('Invalid login credentials')) {
        // Check if this is the admin account
        if (email === 'admin@nemsu.edu.ph') {
          toast.error('Invalid credentials for admin account. Try clicking the "Setup" button below to recreate the admin account.');
        } else {
          toast.error('Invalid email or password. Please check your credentials or sign up for a new account.');
          setShowLoginHelp(true); // Show help section
        }
      } else if (error.message?.includes('Email not confirmed')) {
        toast.error('Please confirm your email address before logging in.');
      } else if (error.message?.includes('User not found in database')) {
        toast.error('Your account exists but data is missing. Please contact an administrator or try signing up again.');
      } else if (error.message?.includes('Unauthorized')) {
        toast.error('Authentication failed. Please try logging in again.');
      } else {
        toast.error(error.message || 'Failed to login. Please check the console for details.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignupNotice(null);
    
    if (!email || !password || !name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!signupNameValidation.isValid) {
      toast.error(signupNameValidation.error || 'Please enter a valid full name');
      return;
    }

    const normalizedDepartment = department.trim();
    const normalizedStudentId = studentId.trim();
    const normalizedEmail = email.trim().toLowerCase();

    if (role === 'faculty' && !normalizedDepartment) {
      toast.error('Department is required');
      return;
    }

    if (role === 'student' && !normalizedStudentId) {
      toast.error('Student ID is required for student verification');
      return;
    }

    setIsLoading(true);
    try {
      await signup({
        email: normalizedEmail,
        password,
        name: signupNameValidation.normalizedName,
        role,
        ...(role === 'faculty' && normalizedDepartment && { department: normalizedDepartment }),
        ...(role === 'student' && { studentId: normalizedStudentId })
      });

      const isCoachSignup = role === 'coach';

      if (isCoachSignup) {
        toast.success('Coach signup submitted. An administrator must verify your account before you can log in.');
        setActiveTab('login');
        setEmail(normalizedEmail);
        setPassword('');
        setName('');
        setDepartment('');
        setStudentId('');
        setRole('student');
        setShowLoginHelp(false);
        setSignupNotice(null);
        return;
      }

      toast.success('Account created successfully! Logging you in...');
      
      // Auto-login after successful signup
      try {
        const { data, error: signInError } = await signIn(email, password);
        
        if (signInError || !data?.session) {
          // Fallback: switch to login tab if auto-login fails
          toast.info('Please login with your new credentials.');
          setActiveTab('login');
          setPassword('');
          setName('');
          setDepartment('');
          setStudentId('');
          return;
        }
        
        // Get user data and call onLogin
        const { user } = await getCurrentUser();
        if (user) {
          await onLogin(user);
          return;
        }
      } catch (loginError) {
        console.warn('Auto-login after signup failed:', loginError);
        toast.info('Account created! Please login with your credentials.');
      }
      
      // Fallback: switch to login tab
      setActiveTab('login');
      setPassword('');
      setName('');
      setDepartment('');
      setStudentId('');
      setShowLoginHelp(false);
      setSignupNotice(null);
    } catch (error: any) {
      const message = error?.message || 'Failed to create account';
      setSignupNotice(message);
    } finally {
      setIsLoading(false);
    }
  };

  /* helper for field wrapper glow */
  const fieldGlow = useCallback(
    (fieldName: string) =>
      focusedField === fieldName
        ? 'ring-2 ring-cyan-400/60 shadow-[0_0_15px_rgba(34,211,238,0.25)]'
        : '',
    [focusedField],
  );

  return (
    <div className="login-page-root min-h-screen w-full flex items-center justify-center relative overflow-hidden px-3 py-14 sm:p-4">
      {/* ====== ANIMATED GRADIENT BACKGROUND ====== */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 login-bg-shift" />
      {/* Neon accent blobs */}
      <div className="absolute -top-32 -left-32 w-[260px] h-[260px] sm:w-[420px] sm:h-[420px] bg-emerald-500/20 blur-[80px] sm:blur-[100px] rounded-full login-blob-drift" />
      <div className="absolute -bottom-24 -right-24 w-[240px] h-[240px] sm:w-[380px] sm:h-[380px] bg-purple-600/20 blur-[80px] sm:blur-[100px] rounded-full login-blob-drift-reverse" />
      <div className="absolute top-1/3 right-1/4 w-40 h-40 sm:w-60 sm:h-60 bg-cyan-500/10 blur-[60px] sm:blur-[80px] rounded-full login-blob-pulse" />

      {/* Grid overlay for e-sports vibe */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.15) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.15) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
        }}
      />

      {/* ====== FLOATING SPORT ICONS ====== */}
      {floatingIcons.map((fi) => {
        const { Icon, color, size } = SPORT_ICONS[fi.iconIdx];
        return (
          <div
            key={fi.id}
            className={`absolute pointer-events-none ${color} opacity-20 login-float-icon`}
            style={{
              left: `${fi.x}%`,
              top: `${fi.y}%`,
              animationDelay: `${fi.delay}s`,
              animationDuration: `${fi.duration}s`,
              transform: `scale(${fi.scale})`,
            }}
          >
            <Icon size={size} />
          </div>
        );
      })}

      {/* ====== MAIN CARD ====== */}
      <Card className="relative z-10 w-full max-w-[calc(100vw-1.5rem)] sm:max-w-md backdrop-blur-2xl bg-white/[0.07] shadow-[0_8px_60px_rgba(0,0,0,0.45)] rounded-2xl sm:rounded-3xl border border-white/10 login-card-entrance">
        <CardHeader className="text-center space-y-2 sm:space-y-3 pb-2 px-4 sm:px-6">
          {/* Logo with neon ring */}
          <div className="mb-1 sm:mb-2 flex justify-center">
            <div className="relative group">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-tr from-emerald-400 via-cyan-400 to-purple-500 opacity-70 blur-md group-hover:opacity-100 transition-opacity duration-500 login-glow-spin" />
              <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center bg-slate-900 border-2 border-white/20 shadow-xl overflow-hidden">
                <img
                  src={logoSrc}
                  alt="App Logo"
                  className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-full"
                />
              </div>
            </div>
          </div>

          <CardTitle className="text-xl sm:text-3xl font-extrabold tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-cyan-300 to-purple-400 drop-shadow-lg">
            SPORTRIX AI
          </CardTitle>

          <CardDescription className="text-xs sm:text-sm text-slate-400 px-2 sm:px-0">
            NEMSU Tandag Sports Equipment Management System
          </CardDescription>

          {/* Decorative sport stripe */}
          <div className="flex items-center justify-center gap-2 pt-1">
            <span className="h-[2px] w-8 bg-gradient-to-r from-transparent to-emerald-400 rounded-full" />
            <Flame size={14} className="text-orange-400 animate-pulse" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">
              Sports &amp; E-Sports
            </span>
            <Gamepad2 size={14} className="text-purple-400 animate-pulse" />
            <span className="h-[2px] w-8 bg-gradient-to-l from-transparent to-purple-400 rounded-full" />
          </div>
        </CardHeader>

        <CardContent className="mt-1 px-3 sm:px-6 pb-4 sm:pb-6">
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as 'login' | 'signup')}
            className="w-full"
          >
            {/* ---- Tab switcher ---- */}
            <TabsList className="grid w-full grid-cols-2 bg-white/[0.06] backdrop-blur-md rounded-xl sm:rounded-2xl p-1 border border-white/10">
              <TabsTrigger
                value="login"
                className="relative rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-cyan-500 data-[state=active]:text-white data-[state=active]:shadow-[0_0_20px_rgba(16,185,129,0.35)] text-slate-400 hover:text-white/80"
              >
                <Zap size={14} className="mr-1 sm:mr-1.5 inline-block" />
                Login
              </TabsTrigger>
              <TabsTrigger
                value="signup"
                className="relative rounded-lg sm:rounded-xl text-xs sm:text-sm font-semibold transition-all duration-300 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-500 data-[state=active]:text-white data-[state=active]:shadow-[0_0_20px_rgba(168,85,247,0.35)] text-slate-400 hover:text-white/80"
              >
                <Trophy size={14} className="mr-1 sm:mr-1.5 inline-block" />
                Sign Up
              </TabsTrigger>
            </TabsList>

            {/* =============== LOGIN TAB =============== */}
            <TabsContent
              value="login"
              className="space-y-4 mt-5 login-tab-enter"
            >
              {pendingEquipmentName && (
                <Alert className="login-tab-enter bg-emerald-500/10 border-emerald-500/20 text-emerald-300">
                  <CheckCircle className="h-4 w-4 text-emerald-400" />
                  <AlertTitle className="text-emerald-300">Borrow Request</AlertTitle>
                  <AlertDescription className="text-emerald-300/80">
                    Sign in to borrow <strong>{pendingEquipmentName}</strong>. The borrow form will open automatically after login.
                  </AlertDescription>
                </Alert>
              )}
              {showLoginHelp && (
                <Alert variant="destructive" className="login-tab-enter">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Can't Login?</AlertTitle>
                  <AlertDescription>
                    If you don’t have an account, click <strong>“Sign Up”</strong>.
                  </AlertDescription>
                </Alert>
              )}
              {loginNotice && (
                <Alert className="login-tab-enter bg-amber-500/10 border-amber-500/30 text-amber-200">
                  <AlertCircle className="h-4 w-4 text-amber-300" />
                  <AlertTitle className="text-amber-100">{loginNotice.title}</AlertTitle>
                  <AlertDescription className="text-amber-100/85">
                    {loginNotice.description}
                  </AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleLogin} className="space-y-4">
                {/* Email */}
                <div className={`space-y-1.5 rounded-xl transition-all duration-300 ${fieldGlow('login-email')}`}>
                  <Label className="text-slate-300 text-xs font-semibold uppercase tracking-wider pl-1">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="your.email@nemsu.edu.ph"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('login-email')}
                    onBlur={() => setFocusedField(null)}
                    className="bg-white/[0.06] border-white/10 text-white placeholder:text-slate-500 rounded-xl hover:border-cyan-500/40 focus:border-cyan-400/60 transition-all duration-300"
                    required
                  />
                </div>

                {/* Password */}
                <div className={`space-y-1.5 rounded-xl transition-all duration-300 ${fieldGlow('login-password')}`}>
                  <Label className="text-slate-300 text-xs font-semibold uppercase tracking-wider pl-1">Password</Label>
                  <div className="relative">
                    <Input
                      id="login-password"
                      type={showLoginPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setFocusedField('login-password')}
                      onBlur={() => setFocusedField(null)}
                      className="pr-10 bg-white/[0.06] border-white/10 text-white placeholder:text-slate-500 rounded-xl hover:border-cyan-500/40 focus:border-cyan-400/60 transition-all duration-300"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowLoginPassword(!showLoginPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-300 transition-colors"
                    >
                      {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  className="w-full py-4 sm:py-5 rounded-xl text-sm sm:text-base font-bold shadow-lg bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 hover:shadow-[0_0_30px_rgba(16,185,129,0.4)] active:scale-[0.98] transition-all duration-300 text-white border-0"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="animate-spin" size={18} />
                      Logging in...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Zap size={18} />
                      Login
                    </span>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <p className="text-center text-xs text-slate-500 pt-1">
                Don't have an account?{' '}
                <button type="button" onClick={() => setActiveTab('signup')} className="text-purple-400 hover:text-purple-300 font-semibold transition-colors underline underline-offset-2">
                  Sign Up
                </button>
              </p>
            </TabsContent>

            {/* =============== SIGN UP TAB =============== */}
            <TabsContent
              value="signup"
              className="space-y-4 mt-5 login-tab-enter"
            >
              {pendingEquipmentName && (
                <Alert className="login-tab-enter bg-purple-500/10 border-purple-500/20 text-purple-300">
                  <CheckCircle className="h-4 w-4 text-purple-400" />
                  <AlertTitle className="text-purple-300">Borrow Request</AlertTitle>
                  <AlertDescription className="text-purple-300/80">
                    Sign up to borrow <strong>{pendingEquipmentName}</strong>. The borrow form will open automatically after signup.
                  </AlertDescription>
                </Alert>
              )}

              {signupNotice && (
                <Alert variant="destructive" className="login-tab-enter border-red-500/40 bg-red-500/10 text-red-100">
                  <AlertCircle className="h-4 w-4 text-red-300" />
                  <AlertTitle className="text-red-100">Signup failed</AlertTitle>
                  <AlertDescription className="text-red-100/90">
                    {signupNotice}
                  </AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSignup} className="space-y-3.5">
                {/* Role */}
                <div className={`space-y-1.5 rounded-xl transition-all duration-300 ${fieldGlow('signup-role')}`}>
                  <Label className="text-slate-300 text-xs font-semibold uppercase tracking-wider pl-1">
                    Role <span className="text-pink-300">*</span>
                  </Label>
                  <select
                    className="w-full px-3 py-2.5 rounded-xl bg-white/[0.06] border border-white/10 text-white hover:border-purple-500/40 focus:border-purple-400/60 focus:ring-2 focus:ring-purple-400/40 transition-all duration-300 outline-none appearance-none cursor-pointer"
                    value={role}
                    onChange={(e) => setRole(e.target.value as UserRole)}
                    onFocus={() => setFocusedField('signup-role')}
                    onBlur={() => setFocusedField(null)}
                    required
                  >
                    <option value="student" className="bg-slate-900">Student</option>
                    <option value="coach" className="bg-slate-900">Coach</option>
                    <option value="faculty" className="bg-slate-900">P.E. Instructor</option>
                  </select>
                  {role === 'coach' && (
                    <p className="pl-1 text-xs text-slate-400">
                      Coach accounts require admin verification. You will receive an approval email once your account is activated.
                    </p>
                  )}
                </div>

                {/* Full Name */}
                <div className={`space-y-1.5 rounded-xl transition-all duration-300 ${fieldGlow('signup-name')}`}>
                  <Label className="text-slate-300 text-xs font-semibold uppercase tracking-wider pl-1">
                    Full Name <span className="text-pink-300">*</span>
                  </Label>
                  <Input
                    placeholder="Juan Dela Cruz"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onFocus={() => setFocusedField('signup-name')}
                    onBlur={() => setFocusedField(null)}
                    className={`bg-white/[0.06] text-white placeholder:text-slate-500 rounded-xl transition-all duration-300 ${name && !signupNameValidation.isValid ? 'border-red-400/70 focus:border-red-400' : 'border-white/10 hover:border-purple-500/40 focus:border-purple-400/60'}`}
                    required
                  />
                  {name && (
                    <p className={`text-xs pl-1 ${signupNameValidation.isValid ? 'text-emerald-300' : 'text-red-300'}`}>
                      {signupNameValidation.isValid
                        ? 'Name format looks valid.'
                        : signupNameValidation.error}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className={`space-y-1.5 rounded-xl transition-all duration-300 ${fieldGlow('signup-email')}`}>
                  <Label className="text-slate-300 text-xs font-semibold uppercase tracking-wider pl-1">
                    Email <span className="text-pink-300">*</span>
                  </Label>
                  <Input
                    type="email"
                    placeholder="your.email@nemsu.edu.ph"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('signup-email')}
                    onBlur={() => setFocusedField(null)}
                    className="bg-white/[0.06] border-white/10 text-white placeholder:text-slate-500 rounded-xl hover:border-purple-500/40 focus:border-purple-400/60 transition-all duration-300"
                    required
                  />
                </div>

                {/* Password */}
                <div className={`space-y-1.5 rounded-xl transition-all duration-300 ${fieldGlow('signup-password')}`}>
                  <Label className="text-slate-300 text-xs font-semibold uppercase tracking-wider pl-1">
                    Password <span className="text-pink-300">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      type={showSignupPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onFocus={() => setFocusedField('signup-password')}
                      onBlur={() => setFocusedField(null)}
                      className="pr-10 bg-white/[0.06] border-white/10 text-white placeholder:text-slate-500 rounded-xl hover:border-purple-500/40 focus:border-purple-400/60 transition-all duration-300"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowSignupPassword(!showSignupPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-purple-300 transition-colors"
                    >
                      {showSignupPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Conditional fields */}
                {role === 'faculty' && (
                  <div className={`space-y-1.5 rounded-xl transition-all duration-300 login-tab-enter ${fieldGlow('signup-dept')}`}>
                    <Label className="text-slate-300 text-xs font-semibold uppercase tracking-wider pl-1">
                      Department <span className="text-pink-300">*</span>
                    </Label>
                    <Input
                      placeholder="Physical Education"
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      onFocus={() => setFocusedField('signup-dept')}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="bg-white/[0.06] border-white/10 text-white placeholder:text-slate-500 rounded-xl hover:border-purple-500/40 focus:border-purple-400/60 transition-all duration-300"
                    />
                  </div>
                )}

                {role === 'student' && (
                  <div className={`space-y-1.5 rounded-xl transition-all duration-300 login-tab-enter ${fieldGlow('signup-sid')}`}>
                    <Label className="text-slate-300 text-xs font-semibold uppercase tracking-wider pl-1">
                      Student ID Number <span className="text-pink-300">*</span>
                    </Label>
                    <Input
                      placeholder="24-12345"
                      value={studentId}
                      onChange={(e) => setStudentId(e.target.value)}
                      onFocus={() => setFocusedField('signup-sid')}
                      onBlur={() => setFocusedField(null)}
                      required
                      className="bg-white/[0.06] border-white/10 text-white placeholder:text-slate-500 rounded-xl hover:border-purple-500/40 focus:border-purple-400/60 transition-all duration-300"
                    />
                  </div>
                )}

                {/* Submit */}
                <Button
                  type="submit"
                  className="w-full py-4 sm:py-5 rounded-xl text-sm sm:text-base font-bold shadow-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 hover:shadow-[0_0_30px_rgba(168,85,247,0.4)] active:scale-[0.98] transition-all duration-300 text-white border-0"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <RefreshCw className="animate-spin" size={18} />
                      Creating Account...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Trophy size={18} />
                      Create Account
                    </span>
                  )}
                </Button>
              </form>

              {/* Divider */}
              <p className="text-center text-xs text-slate-500 pt-1">
                Already have an account?{' '}
                <button type="button" onClick={() => setActiveTab('login')} className="text-cyan-400 hover:text-cyan-300 font-semibold transition-colors underline underline-offset-2">
                  Login
                </button>
              </p>
            </TabsContent>

          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}